/**
 * Customer documents — uploads tied to a service request.
 *
 * Endpoints:
 *   GET  /api/v1/customer/service-requests/{id}/documents                              → list docs for a SR
 *   POST /api/v1/customer/service-requests/{id}/documents/initiate-upload              → get presigned S3 PUT URL
 *   POST /api/v1/customer/service-requests/{id}/documents/{doc_id}/confirm-upload      → confirm S3 upload landed
 *   GET  /api/v1/customer/service-requests/{id}/documents/{doc_id}/download-url        → presigned S3 GET URL
 *
 * Constraints (from parent CLAUDE.md):
 *   - Max 20 MB per file. Validate client-side before POSTing.
 */
import {
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ApiError, confirmUploadWithRetry, createKeyFactory, type Schemas } from '@finmin/api-client';
import { apiClient } from '@/lib/api';
import { queryClient } from './query-client';
import { serviceRequestKeys } from './service-requests';

// ---------- types -----------------------------------------------------------

export type DocumentResponse = Schemas['DocumentResponse'];
export type DocumentReviewStatus = Schemas['DocumentReviewStatus'];

export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024; // 20 MB

// ---------- query keys ------------------------------------------------------

export const documentKeys = createKeyFactory('customer.documents', {
  forRequest: (serviceRequestId: string) => ['for-request', serviceRequestId] as const,
});

// ---------- customer visibility gate ----------------------------------------

/**
 * Documents the customer is allowed to see on a service request.
 *
 * The provider's `draft_computation` is an INTERNAL review artifact between the
 * provider and the FinminOS admin team — it is NEVER shown to the customer. The
 * customer's deliverable is the final `acknowledgement`, which (like every other
 * document type) passes through and IS shown. The backend returns every document
 * including the draft, so we filter the draft out here on every customer fetch.
 */
export function selectCustomerVisibleDocuments(docs: DocumentResponse[]): DocumentResponse[] {
  return docs.filter((doc) => doc.document_type !== 'draft_computation');
}

// ---------- raw API functions -----------------------------------------------

/** GET /api/v1/customer/service-requests/{id}/documents — docs uploaded for this SR. */
export async function listDocumentsForRequest(serviceRequestId: string, signal?: AbortSignal) {
  const { data } = await apiClient.get<DocumentResponse[]>(
    `/api/v1/customer/service-requests/${serviceRequestId}/documents`,
    { signal },
  );
  return data;
}

export interface InitiateUploadParams {
  filename: string;
  content_type: string;
  file_size: number;
  document_type: string;
  document_label?: string;
}

interface InitiateUploadResponse {
  document_id: string;
  upload_url: string;
  expires_in: number;
}

interface DownloadUrlResponse {
  download_url: string;
  expires_in: number;
  filename: string;
  content_type: string;
}

/** POST /api/v1/customer/service-requests/{id}/documents/initiate-upload — request a presigned S3 PUT URL. */
async function initiateUpload(
  serviceRequestId: string,
  params: InitiateUploadParams,
): Promise<InitiateUploadResponse> {
  const { data } = await apiClient.post<InitiateUploadResponse>(
    `/api/v1/customer/service-requests/${serviceRequestId}/documents/initiate-upload`,
    params,
  );
  return data;
}

/** PUTs the file directly to S3 using the presigned URL. */
function uploadToS3(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new ApiError(`S3 upload failed (${xhr.status})`, xhr.status));
    };
    xhr.onerror = () => reject(new ApiError('Network error during upload', 0));
    xhr.send(file);
  });
}

/** POST /api/v1/customer/service-requests/{id}/documents/{doc_id}/confirm-upload — finalize after the S3 PUT. */
async function confirmUpload(
  serviceRequestId: string,
  documentId: string,
): Promise<DocumentResponse> {
  const { data } = await apiClient.post<DocumentResponse>(
    `/api/v1/customer/service-requests/${serviceRequestId}/documents/${documentId}/confirm-upload`,
  );
  return data;
}

/** Upload one file: initiate → PUT to S3 → confirm. */
export async function uploadDocument(
  serviceRequestId: string,
  file: File,
  documentType: string,
  onProgress?: (percent: number) => void,
  documentLabel?: string,
): Promise<DocumentResponse> {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new ApiError(`File exceeds 20 MB limit (got ${(file.size / 1024 / 1024).toFixed(1)} MB)`, 413);
  }

  const { upload_url, document_id } = await initiateUpload(serviceRequestId, {
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    file_size: file.size,
    document_type: documentType,
    document_label: documentLabel?.trim(),
  });

  await uploadToS3(upload_url, file, file.type || 'application/octet-stream', onProgress);

  // Complete only once confirm returns 200; 502/503/504 are retried.
  return confirmUploadWithRetry(() => confirmUpload(serviceRequestId, document_id));
}

/** A document's bytes plus its server-provided filename, ready to save to disk. */
export interface DocumentDownload {
  blob: Blob;
  filename: string;
}

/** GET /api/v1/customer/service-requests/{id}/documents/{doc_id}/download-url — presigned S3 GET URL. */
async function getDownloadUrl(
  serviceRequestId: string,
  documentId: string,
): Promise<DownloadUrlResponse> {
  const { data } = await apiClient.get<DownloadUrlResponse>(
    `/api/v1/customer/service-requests/${serviceRequestId}/documents/${documentId}/download-url`,
  );
  return data;
}

/**
 * Fetch a document's bytes: get a presigned S3 GET URL, then fetch it directly
 * (no auth header needed — the URL itself is the credential).
 */
export async function downloadDocument(
  serviceRequestId: string,
  documentId: string,
): Promise<DocumentDownload> {
  const { download_url, filename, content_type } = await getDownloadUrl(serviceRequestId, documentId);
  const response = await fetch(download_url);
  if (!response.ok) throw new ApiError(`Download failed (${response.status})`, response.status);
  const blob = await response.blob();
  return { blob: new Blob([blob], { type: content_type }), filename };
}

// ---------- hooks -----------------------------------------------------------

export function useDocumentsForRequest(
  serviceRequestId: string,
  options?: Omit<UseQueryOptions<DocumentResponse[], ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<DocumentResponse[], ApiError>({
    queryKey: documentKeys.forRequest(serviceRequestId),
    queryFn: ({ signal }) => listDocumentsForRequest(serviceRequestId, signal),
    enabled: Boolean(serviceRequestId),
    ...options,
    // Always gate unapproved provider drafts out of the customer's view — even
    // if a caller passes its own options, this stays enforced.
    select: selectCustomerVisibleDocuments,
  });
}

export function useUploadDocument(
  options?: UseMutationOptions<
    DocumentResponse,
    ApiError,
    { serviceRequestId: string; file: File; documentType: string; onProgress?: (p: number) => void; documentLabel?: string }
  >,
) {
  return useMutation<
    DocumentResponse,
    ApiError,
    { serviceRequestId: string; file: File; documentType: string; onProgress?: (p: number) => void; documentLabel?: string }
  >({
    mutationFn: ({ serviceRequestId, file, documentType, onProgress, documentLabel }) =>
      uploadDocument(serviceRequestId, file, documentType, onProgress, documentLabel),
    ...options,
    // After `...options` so a caller's callbacks can't skip the refetch, and
    // on failure too: a failed confirm may already have dropped the document.
    onSettled: (data, err, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.forRequest(variables.serviceRequestId) });
      queryClient.invalidateQueries({ queryKey: serviceRequestKeys.detail(variables.serviceRequestId) });
      options?.onSettled?.(data, err, variables, onMutateResult, context);
    },
  });
}

/** Fetches a document's bytes via a presigned S3 URL so the caller can save them to disk. */
export function useDownloadDocument() {
  return useMutation<DocumentDownload, ApiError, { serviceRequestId: string; documentId: string }>({
    mutationFn: ({ serviceRequestId, documentId }) =>
      downloadDocument(serviceRequestId, documentId),
  });
}
